import axios from "axios";
import { fetchAuthSession } from "aws-amplify/auth";
import { config } from "../../config.js";

const baseUrl = config.controlPlaneAPI + "/threat-designer";

const instance = axios.create({
  baseURL: baseUrl,
});

instance.interceptors.request.use(async (config) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens.idToken.toString();
    config.headers.Authorization = `Bearer ${token}`;

    // Add cache-busting timestamp to GET requests to prevent browser caching
    if (config.method === "get") {
      config.params = {
        ...config.params,
        _t: Date.now(),
      };
    }

    return config;
  } catch (error) {
    return Promise.reject(error);
  }
});

async function deleteTm(id) {
  const statsPath = `/${id}`;
  return instance.delete(statsPath);
}

async function stopTm(id, sessionId) {
  const statsPath = `/${id}/session/${sessionId}`;
  return instance.delete(statsPath);
}

async function startThreatModeling(
  key = null,  // Now accepts array of S3 paths or single path
  iteration = null,
  reasoning = false,
  title = null,
  description = null,
  assumptions = null,
  approvedTools = null,
  replay = false,
  id = null,
  instructions = null,
  smePdfPath = null  // NEW: SME PDF S3 path
) {
  const statsPath = "";
  const postData = {
    // Support both single file (backward compatibility) and multiple files
    s3_locations: Array.isArray(key) ? key : (key ? [key] : []),
    iteration,
    title,
    description,
    assumptions,
    approved_tools: approvedTools,
    replay,
    id,
    reasoning,
    instructions,
    sme_pdf_path: smePdfPath,  // NEW: Include SME PDF path
  };
  return instance.post(statsPath, postData);
}

async function updateTm(id, payload, clientTimestamp = null) {
  const statsPath = `/${id}`;
  const requestPayload = { ...payload };

  // Add client timestamp for conflict detection
  if (clientTimestamp) {
    requestPayload.client_last_modified_at = clientTimestamp;
  }

  return instance.put(statsPath, requestPayload);
}

async function restoreTm(id) {
  const statsPath = `/restore/${id}`;
  return instance.put(statsPath);
}

async function generateUrl(fileType) {
  const statsPath = "/upload";
  const postData = {
    file_type: fileType,
  };
  return instance.post(statsPath, postData);
}

async function getDownloadUrl(threatModelId) {
  const downloadPath = "/download";
  const postData = {
    threat_model_id: threatModelId,
  };
  try {
    const response = await instance.post(downloadPath, postData);
    const presignedUrl = response.data;

    const fileResponse = await axios.get(presignedUrl, {
      responseType: "blob",
    });

    return fileResponse.data;
  } catch (error) {
    return Promise.reject(error);
  }
}

async function getDownloadUrlsBatch(threatModelIds) {
  const downloadPath = "/download/batch";
  const postData = {
    threat_model_ids: threatModelIds,
  };
  try {
    const response = await instance.post(downloadPath, postData);
    return response.data.results;
  } catch (error) {
    return Promise.reject(error);
  }
}

async function getThreatModelingStatus(id) {
  const statsPath = `/status/${id}`;
  return instance.get(statsPath);
}

async function getThreatModelingTrail(id) {
  const statsPath = `/trail/${id}`;
  return instance.get(statsPath);
}

async function getThreatModelingResults(id) {
  const statsPath = `/${id}`;
  return instance.get(statsPath);
}

async function getThreatModelingAllResults(limit = null, cursor = null, filter = null) {
  const params = new URLSearchParams();

  if (limit !== null) {
    params.append("limit", limit);
  }
  if (cursor !== null) {
    params.append("cursor", cursor);
  }
  if (filter !== null) {
    params.append("filter", filter);
  }

  const queryString = params.toString();
  const statsPath = queryString ? `/all?${queryString}` : `/all`;

  return instance.get(statsPath);
}

async function getDownloadUrlsMultiple(threatModelId) {
  // Download all architecture diagrams for a threat model
  const downloadPath = "/download/all";
  const postData = {
    threat_model_id: threatModelId,
  };
  
  try {
    const response = await instance.post(downloadPath, postData);
    const presignedUrls = response.data.presigned_urls;

    // Download all diagrams in parallel
    const downloadPromises = presignedUrls.map(url =>
      axios.get(url, { responseType: "blob" })
    );
    
    const fileResponses = await Promise.all(downloadPromises);
    return fileResponses.map(r => r.data);
  } catch (error) {
    console.error("Error downloading multiple diagrams:", error);
    // Fallback to single diagram
    try {
      const singleBlob = await getDownloadUrl(threatModelId);
      return [singleBlob];
    } catch (fallbackError) {
      return [];
    }
  }
}

export {
  getThreatModelingStatus,
  getThreatModelingResults,
  startThreatModeling,
  generateUrl,
  updateTm,
  getDownloadUrl,
  getDownloadUrlsBatch,
  getDownloadUrlsMultiple,
  deleteTm,
  getThreatModelingAllResults,
  getThreatModelingTrail,
  restoreTm,
  stopTm,
};
